import React from 'react';
import { Icon } from '@iconify/react';

// 頁數選項
const pageCountOptions = [
  { id: '3', name: '3 頁' },
  { id: '5', name: '5 頁' },
  { id: '7', name: '7 頁' },
  { id: '10', name: '10 頁' }
];

// 語氣風格選項
const toneOptions = [
  { id: 'default', name: '預設', icon: 'mdi:account-voice' },
  { id: 'professional', name: '專業', icon: 'mdi:briefcase' },
  { id: 'casual', name: '輕鬆', icon: 'mdi:emoticon-happy' },
  { id: 'humorous', name: '幽默', icon: 'mdi:emoticon-lol' }
];

// 語言選項
const languageOptions = [
  { id: 'zh-TW', name: '繁體中文' },
  { id: 'en', name: 'English'},
];

/**
 * 選項按鈕組件
 */
function OptionButton({ selected, onClick, children, icon, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
        transition-all duration-200 ease-out
        ${selected
          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/25'
          : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
        }
        ${className}
      `}
    >
      {icon && <Icon icon={icon} className="text-base" />}
      {children}
      {selected && (
        <Icon icon="mdi:check" className="text-sm ml-auto" />
      )}
    </button>
  );
}

/**
 * 小型選項按鈕（用於語言等）
 */
function CompactOption({ selected, onClick, children, flag }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium
        transition-all duration-200
        ${selected
          ? 'bg-blue-50 text-blue-600 border-2 border-blue-500'
          : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
        }
      `}
    >
      {flag && <span className="text-sm">{flag}</span>}
      {children}
    </button>
  );
}

/**
 * 設定區塊標題
 */
function SettingLabel({ children, icon }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon && <Icon icon={icon} className="text-slate-400 text-sm" />}
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {children}
      </span>
    </div>
  );
}

/**
 * ContentSettings 主組件
 *
 * @param {Object} props
 * @param {string} props.pageCount - 當前選擇的頁數
 * @param {function} props.onPageCountChange - 頁數變更回調
 * @param {string} props.tone - 當前選擇的語氣
 * @param {function} props.onToneChange - 語氣變更回調
 * @param {string} props.language - 當前選擇的語言
 * @param {function} props.onLanguageChange - 語言變更回調
 * @param {string} props.className - 額外的 className
 */
function ContentSettings({
  pageCount = '5',
  onPageCountChange,
  tone = 'default',
  onToneChange,
  language = 'zh-TW',
  onLanguageChange,
  className = ''
}) {
  return (
    <div className={`bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-6 pb-4 border-b border-slate-200/60">
        <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
          <Icon icon="mdi:tune-variant" className="text-white text-lg" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-slate-800">內容設定</h3>
          <p className="text-[11px] text-slate-400">自訂你的輪播內容</p>
        </div>
      </div>

      {/* Page Count */}
      <div className="mb-6">
        <SettingLabel icon="mdi:file-multiple">輪播頁數</SettingLabel>
        <div className="flex gap-2">
          {pageCountOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => onPageCountChange?.(option.id)}
              className={`
                flex-1 py-3 rounded-xl text-sm font-semibold
                transition-all duration-200
                ${pageCount === option.id
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-500'
                }
              `}
            >
              {option.name}
            </button>
          ))}
        </div>
      </div>

      {/* Language */}
      <div>
        <SettingLabel icon="mdi:translate">輸出語言</SettingLabel>
        <div className="flex flex-wrap gap-2">
          {languageOptions.map((option) => (
            <CompactOption
              key={option.id}
              selected={language === option.id}
              onClick={() => onLanguageChange?.(option.id)}
              flag={option.flag}
            >
              {option.name}
            </CompactOption>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ContentSettings;
export { ContentSettings, OptionButton, CompactOption, SettingLabel };
